import { useEffect, useRef } from 'react';
import { MAX_REPORT_ZOOM, MIN_REPORT_ZOOM } from '../context/ZoomContext';

const clampZoom = (zoom: number) => Math.min(Math.max(zoom, MIN_REPORT_ZOOM), MAX_REPORT_ZOOM);

export function useReportZoom(setZoom: React.Dispatch<React.SetStateAction<number>>) {
    const lastNoCtrlWheelTime = useRef<number>(0);
    // Accumulate wheel deltas and apply a single setZoom per animation frame, so
    // a burst of trackpad-pinch wheel events collapses into one state update per
    // frame instead of dozens.
    const pendingWheelDelta = useRef<number>(0);
    const rafId = useRef<number | null>(null);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey || e.metaKey) {
                if (e.key === '=' || e.key === '+') {
                    e.preventDefault();
                    setZoom((prev) => clampZoom(prev + 0.1));
                } else if (e.key === '-') {
                    e.preventDefault();
                    setZoom((prev) => clampZoom(prev - 0.1));
                } else if (e.key === '0') {
                    e.preventDefault();
                    setZoom(1);
                }
            }
        };

        const flushWheelZoom = () => {
            rafId.current = null;
            const delta = pendingWheelDelta.current;
            pendingWheelDelta.current = 0;
            if (delta === 0) return;
            setZoom((prev) => clampZoom(prev - delta * 0.001));
        };

        const handleWheel = (e: WheelEvent) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();

                if (Date.now() - lastNoCtrlWheelTime.current < 250) {
                    return;
                }

                pendingWheelDelta.current += e.deltaY;
                if (rafId.current === null) {
                    rafId.current = window.requestAnimationFrame(flushWheelZoom);
                }
            } else {
                lastNoCtrlWheelTime.current = Date.now();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('wheel', handleWheel);
            if (rafId.current !== null) {
                window.cancelAnimationFrame(rafId.current);
                rafId.current = null;
            }
        };
    }, [setZoom]);
}
